import { Defer } from '@streamr/utils';
import { EventEmitter } from 'eventemitter3';

const STREAMR_TV_API_MESSAGE_MAGIC_COOKIE = 'streamr-tv-api-message';
function isStreamrTvApiMessage(candidate) {
    return (typeof candidate === 'object' &&
        candidate !== null &&
        'magicCookie' in candidate &&
        candidate.magicCookie === STREAMR_TV_API_MESSAGE_MAGIC_COOKIE);
}
class StreamrTvApi extends EventEmitter {
    nextGetterRequestId = 0;
    ongoingGetterRequests = new Map();
    context = new Defer();
    latestContext;
    videoProcessingHandler;
    frameIds = [];
    constructor() {
        super();
        window.addEventListener('message', (event) => {
            const { data: apiMessage } = event;
            if (!isStreamrTvApiMessage(apiMessage)) {
                return;
            }
            switch (apiMessage.operation) {
                case 'networkMessageReceived': {
                    this.emit('networkMessageReceived', apiMessage.payload, apiMessage.metadata);
                    break;
                }
                case 'pluginMessageReceived': {
                    this.emit('pluginMessageReceived', {
                        payload: apiMessage.payload,
                        metadata: {
                            topic: apiMessage.metadata.topic,
                            publisherPluginId: apiMessage.metadata.publisherPluginId,
                            publisherFrameId: apiMessage.metadata.publisherFrameId,
                        },
                    });
                    break;
                }
                case 'purchaseCompleted': {
                    const purchase = apiMessage.purchase;
                    this.emit('purchaseCompleted', purchase);
                    break;
                }
                case 'getterResponse': {
                    const resolveReturnValue = this.ongoingGetterRequests.get(apiMessage.requestId);
                    // biome-ignore lint/style/noNonNullAssertion: there is always request for each response
                    resolveReturnValue.resolve(apiMessage.value);
                    break;
                }
                case 'initializationResponse': {
                    this.latestContext = apiMessage.pluginContext;
                    void this.context.resolve(apiMessage.pluginContext);
                    break;
                }
                case 'pluginContextChanged': {
                    this.latestContext = apiMessage.pluginContext;
                    void this.context.resolve(apiMessage.pluginContext);
                    this.emit('pluginContextChanged', apiMessage.pluginContext);
                    break;
                }
                case 'videoFrame': {
                    console.log('StreamrTvApi videoFrame', apiMessage.imageBitmap);
                    this.frameIds.push(apiMessage.frameId);
                    this.videoProcessingHandler?.(apiMessage.imageBitmap);
                    break;
                }
            }
        });
        this.postOperationMessageToParentWindow('initializationRequest');
    }
    // API methods
    publishNetworkMessage(payload) {
        this.postOperationMessageToParentWindow('publishNetworkMessage', {
            payload,
        });
    }
    publishPluginMessage(topic, payload) {
        this.postOperationMessageToParentWindow('publishPluginMessage', {
            payload,
            metadata: {
                topic,
            },
        });
    }
    /**
     * Subscribe to plugin messages.
     * @param topic - Topic name to subscribe to
     * @param options - Optional filters for sender plugin and frame
     */
    subscribeToPluginMessages(topic, options) {
        this.postOperationMessageToParentWindow('subscribeToPluginMessages', {
            metadata: {
                topic,
                publisherPluginId: options?.publisherPluginId,
                publisherFrameId: options?.publisherFrameId,
            },
        });
    }
    /**
     * Unsubscribe from plugin messages.
     * @param topic - Topic name to unsubscribe from
     * @param options - Optional filters for sender plugin and frame (must match the original subscription)
     */
    unsubscribeFromPluginMessages(topic, options) {
        this.postOperationMessageToParentWindow('unsubscribeFromPluginMessages', {
            metadata: {
                topic,
                publisherPluginId: options?.publisherPluginId,
                publisherFrameId: options?.publisherFrameId,
            },
        });
    }
    hideFrame() {
        this.postOperationMessageToParentWindow('hideFrame');
    }
    showFrame() {
        this.postOperationMessageToParentWindow('showFrame');
    }
    startPurchase(metadata) {
        this.postOperationMessageToParentWindow('startPurchase', {
            metadata,
        });
    }
    setVideoProcessingHandler(callback) {
        console.log('StreamrTvApi setVideoProcessingHandler');
        this.videoProcessingHandler = callback;
        this.postOperationMessageToParentWindow('setVideoProcessingHandler');
    }
    reportVideoFrameProcessed(imageBitmap) {
        console.log('StreamrTvApi reportVideoFrameProcessed');
        const frameId = this.frameIds.shift();
        if (!frameId) {
            throw new Error('No frameId found');
        }
        this.postOperationMessageToParentWindowTransfer('reportVideoFrameProcessed', [imageBitmap], {
            imageBitmap,
            frameId,
        });
    }
    getPluginContext() {
        if (this.latestContext !== undefined) {
            return Promise.resolve(this.latestContext);
        }
        return this.context;
    }
    sendGetterRequest(methodName) {
        const requestId = this.nextGetterRequestId;
        const promise = new Defer();
        this.ongoingGetterRequests.set(requestId, promise);
        this.postOperationMessageToParentWindow('getterRequest', {
            requestId,
            methodName,
        });
        this.nextGetterRequestId++;
        return promise;
    }
    postOperationMessageToParentWindow(operation, ...rest) {
        const [params = {}] = rest;
        window.parent.postMessage({
            magicCookie: STREAMR_TV_API_MESSAGE_MAGIC_COOKIE,
            operation,
            ...params,
        }, '*');
    }
    postOperationMessageToParentWindowTransfer(operation, transferable, ...rest) {
        const [params = {}] = rest;
        window.parent.postMessage({
            magicCookie: STREAMR_TV_API_MESSAGE_MAGIC_COOKIE,
            operation,
            ...params,
        }, '*', transferable);
    }
}

export { StreamrTvApi as S, STREAMR_TV_API_MESSAGE_MAGIC_COOKIE as a, isStreamrTvApiMessage as i };
//# sourceMappingURL=StreamrTvApi-Dyd-3lEb.mjs.map
